import { Combobox as BaseCombobox } from '@base-ui/react/combobox';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  BuildingIcon,
  CheckIcon,
  GlobeIcon,
  MapPinIcon,
  MinusIcon,
  PlusIcon,
  SparkleIcon,
  TagIcon,
  XIcon
} from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import Dropdown from '@/components/ui/Dropdown';
import Tooltip from '@/components/ui/Tooltip';
import { TagPill, type TagPillGroup } from 'src/features/tags/TagPill';

export interface TagPillSelectOption {
  id: string;
  label: string;
  group: TagPillGroup;
}

export interface TagPillSelectGroup {
  slug: TagPillGroup;
  label: string;
  cardinality: 'single' | 'multiple';
  options: TagPillSelectOption[];
}

interface TagPillSelectProps {
  groups: TagPillSelectGroup[];
  value: string[];
  onChange: (ids: string[]) => void | Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  /** Popover copy size. Filter bars use `sm`; the action modal stays `xs`. */
  size?: 'xs' | 'sm';
  /** Keep selected pills on one line and clip the rest. */
  singleLine?: boolean;
  /** Selected ids that are still AI-suggested (unconfirmed pill style). */
  suggestedIds?: string[];
  /** Tooltip text for selected pills, keyed by option id. */
  pillTitles?: Record<string, string>;
  /** AI suggestion metadata for selected ids (confidence + review actions). */
  suggestions?: Record<string, { confidence: number | null }>;
  onApproveSuggestion?: (id: string) => void;
  onDeclineSuggestion?: (id: string) => void;
  /** Notifies the parent when a selected tag is hovered. */
  onHoverSelected?: (id: string | null) => void;
}

const FILTER_ICON = {
  document_type: TagIcon,
  domain: GlobeIcon,
  site: MapPinIcon,
  national_returns: BuildingIcon
} as const;

function suggestionBand(confidence: number | null): { label: string; tooltip: string; classes: string } {
  if (confidence === null)
    return {
      label: 'Low',
      tooltip: 'AI suggested – confidence unknown. Review before accepting.',
      classes: 'bg-red-50 text-red-600 ring-red-500/20'
    };
  const pct = Math.round(confidence * 100);
  if (confidence >= 0.85)
    return {
      label: 'High',
      tooltip: `High confidence (${pct}%) – AI is fairly certain about this.`,
      classes: 'bg-green-50 text-green-700 ring-green-500/20'
    };
  if (confidence >= 0.6)
    return {
      label: 'Medium',
      tooltip: `Medium confidence (${pct}%) – worth a quick check.`,
      classes: 'bg-amber-50 text-amber-700 ring-amber-500/20'
    };
  return {
    label: 'Low',
    tooltip: `Low confidence (${pct}%) – please verify carefully.`,
    classes: 'bg-red-50 text-red-600 ring-red-500/20'
  };
}

function TagOptionRow({
  option,
  selected,
  onToggle,
  size,
  suggestion,
  onApprove,
  onDecline
}: {
  option: TagPillSelectOption;
  selected: boolean;
  onToggle: () => void;
  size: 'xs' | 'sm';
  suggestion?: { confidence: number | null };
  onApprove?: () => void;
  onDecline?: () => void;
}) {
  const ActionIcon = selected ? MinusIcon : PlusIcon;
  const band = suggestion ? suggestionBand(suggestion.confidence) : null;
  return (
    <div
      className={twMerge(
        'group flex w-full items-center justify-between gap-2 rounded-[4px] px-1 py-1 text-content-default',
        size === 'sm' ? 'text-sm' : 'text-xs'
      )}
    >
      <button
        type="button"
        onClick={
          suggestion
            ? undefined
            : (event) => {
                event.stopPropagation();
                onToggle();
              }
        }
        className="flex min-w-0 flex-1 items-center gap-1.5 text-left hover:bg-transparent"
      >
        {option.group === 'domain' ? (
          <GlobeIcon size={12} weight="light" className="shrink-0" />
        ) : option.group === 'site' ? (
          <MapPinIcon size={12} weight="light" className="shrink-0" />
        ) : option.group === 'national_returns' ? (
          <BuildingIcon size={12} weight="light" className="shrink-0" />
        ) : (
          <TagIcon size={12} weight="light" className="shrink-0" />
        )}
        <span className="truncate">{option.label}</span>
      </button>
      {band ? (
        <span className="flex shrink-0 items-center gap-1">
          <span
            title={band.tooltip}
            className={twMerge(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset',
              band.classes
            )}
          >
            <SparkleIcon size={10} weight="fill" />
            {band.label}
          </span>
          {onApprove && (
            <button
              type="button"
              aria-label={`Confirm ${option.label}`}
              title="Confirm"
              onClick={(event) => {
                event.stopPropagation();
                onApprove();
              }}
              className="rounded p-0.5 text-green-700 hover:bg-green-50"
            >
              <CheckIcon size={14} weight="bold" />
            </button>
          )}
          {onDecline && (
            <button
              type="button"
              aria-label={`Decline ${option.label}`}
              title="Decline"
              onClick={(event) => {
                event.stopPropagation();
                onDecline();
              }}
              className="rounded p-0.5 text-content-subtle hover:bg-red-50 hover:text-red-600"
            >
              <XIcon size={14} weight="bold" />
            </button>
          )}
        </span>
      ) : (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
          className="rounded p-0.5 text-content-subtle opacity-0 group-hover:opacity-100"
          aria-label={selected ? `Remove ${option.label}` : `Add ${option.label}`}
        >
          <ActionIcon size={12} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default function TagPillSelect({
  groups,
  value,
  onChange,
  placeholder = 'Not set',
  disabled = false,
  ariaLabel,
  className,
  size = 'xs',
  singleLine = false,
  suggestedIds,
  pillTitles,
  suggestions,
  onApproveSuggestion,
  onDeclineSuggestion,
  onHoverSelected
}: TagPillSelectProps) {
  const [open, setOpen] = useState(false);
  const [chipsElement, setChipsElement] = useState<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState<TagPillGroup[]>([]);
  const [search, setSearch] = useState('');
  const [optimisticValue, setOptimisticValue] = useState<string[] | null>(null);

  const allOptions = groups.flatMap((g) => g.options);
  const selectedValue = optimisticValue ?? value;
  const optionsById = new Map(allOptions.map((option) => [option.id, option]));
  const selected = selectedValue.flatMap((id) => {
    const option = optionsById.get(id);
    return option ? [option] : [];
  });
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const recomputeVisible = useCallback(() => {
    if (!singleLine || !chipsElement || !measureRef.current) return;
    const children = Array.from(measureRef.current.children) as HTMLElement[];
    if (children.length < 2) return;
    const badge = children[children.length - 1];
    const widths = children.slice(0, -1).map((child) => child.offsetWidth);
    if (widths.some((width) => width === 0)) return;
    const style = getComputedStyle(chipsElement);
    const available = chipsElement.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    if (available <= 0) return;
    const gap = 4;
    const badgeWidth = badge.offsetWidth;
    let used = 0;
    let fit = 0;
    for (let index = 0; index < widths.length; index += 1) {
      const reserve = index < widths.length - 1 ? gap + badgeWidth : 0;
      const next = (fit > 0 ? gap : 0) + widths[index];
      if (used + next + reserve <= available) {
        used += next;
        fit += 1;
        continue;
      }
      fit = Math.max(fit, 1);
      break;
    }
    setVisibleCount((current) => (current === fit ? current : fit));
  }, [chipsElement, singleLine, selectedValue, groups]);

  useLayoutEffect(() => {
    if (!singleLine) return;
    recomputeVisible();
    if (!chipsElement || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(recomputeVisible);
    observer.observe(chipsElement);
    return () => observer.disconnect();
  }, [chipsElement, recomputeVisible, singleLine]);

  const shown = singleLine && visibleCount != null ? selected.slice(0, visibleCount) : selected;
  const hiddenCount = singleLine ? Math.max(selected.length - shown.length, 0) : 0;
  const filterSet = new Set(filter);
  const showAllTypes = filter.length === 0;

  useEffect(() => {
    setOptimisticValue(null);
  }, [value]);

  const selectableGroups = useMemo(() => {
    const scoped = showAllTypes ? groups : groups.filter((g) => filterSet.has(g.slug));
    return scoped.map((group) => ({
      ...group,
      items: group.options
        .filter((option) => !selectedValue.includes(option.id))
        .map((option) => ({ ...option, tagGroup: group }))
    }));
  }, [filter, groups, selectedValue, showAllTypes]);

  const updateSelection = (nextValue: string[]) => {
    setOptimisticValue(nextValue);
    void Promise.resolve()
      .then(() => onChange(nextValue))
      .catch(() => setOptimisticValue(null));
  };

  const toggle = (group: TagPillSelectGroup, id: string) => {
    const groupIds = new Set(group.options.map((o) => o.id));
    if (group.cardinality === 'single') {
      const withoutGroup = selectedValue.filter((v) => !groupIds.has(v));
      updateSelection(selectedValue.includes(id) ? withoutGroup : [...withoutGroup, id]);
      return;
    }
    updateSelection(selectedValue.includes(id) ? selectedValue.filter((v) => v !== id) : [...selectedValue, id]);
  };

  const suggestedSet = new Set(suggestedIds ?? []);
  const selectedOptions = useMemo(
    () =>
      selected.map((option) => ({
        ...option,
        group: groups.find((group) => group.slug === option.group)!
      })),
    [groups, selected]
  );
  const allSelectableOptions = useMemo(
    () => groups.flatMap((group) => group.options.map((option) => ({ ...option, group }))),
    [groups]
  );

  return (
    <BaseCombobox.Root
      items={selectableGroups}
      multiple
      autoHighlight
      value={selectedOptions}
      open={open}
      inputValue={search}
      onInputValueChange={(next, eventDetails) => {
        if (eventDetails.reason === 'item-press') {
          eventDetails.cancel();
          return;
        }
        setSearch(next);
      }}
      onOpenChange={(next, eventDetails) => {
        if (!next && eventDetails.reason === 'item-press') {
          eventDetails.cancel();
          return;
        }
        setOpen(next);
        if (!next) {
          setSearch('');
        }
      }}
      onValueChange={(next) => {
        const nextIds = new Set((next ?? []).map((option) => option.id));
        const changed = allSelectableOptions.find(
          (option) => nextIds.has(option.id) !== selectedValue.includes(option.id)
        );
        if (changed) {
          toggle(changed.group, changed.id);
          setSearch('');
        }
      }}
      disabled={disabled}
      modal={false}
      itemToStringLabel={(option) => option.label}
      itemToStringValue={(option) => option.id}
      isItemEqualToValue={(option, current) => option.id === current.id}
    >
      <BaseCombobox.Chips
        ref={setChipsElement}
        className={twMerge(
          'flex w-full items-center gap-1 rounded-lg border-2 border-border-default px-2 py-1',
          singleLine ? 'h-9 flex-nowrap overflow-hidden' : 'min-h-9 flex-wrap',
          disabled
            ? 'cursor-not-allowed border-border-default bg-bluegray-50 opacity-60'
            : 'cursor-text border-border-default bg-white hover:bg-surface-hover focus-within:border-border-active focus-within:bg-surface-hover',
          className
        )}
      >
        {singleLine && (
          <div
            ref={measureRef}
            aria-hidden
            className="pointer-events-none invisible absolute flex h-0 w-max items-center gap-1 overflow-hidden"
          >
            {selected.map((tag) => (
              <span key={tag.id} className="shrink-0">
                <TagPill label={tag.label} group={tag.group} trailing={<XIcon size={12} weight="bold" />} />
              </span>
            ))}
            <span className="inline-flex shrink-0 items-center rounded-full bg-surface-subtle px-2 py-0.5 text-xs">
              +99
            </span>
          </div>
        )}
        {shown.map((tag) => {
          const suggested = suggestedSet.has(tag.id);
          return (
            <BaseCombobox.Chip key={tag.id} className="contents">
              <span
                className={twMerge(
                  'inline-flex max-w-full min-w-0',
                  hiddenCount > 0 && tag.id === shown[shown.length - 1]?.id ? 'shrink' : 'shrink-0'
                )}
              >
                <TagPill
                  label={tag.label}
                  group={tag.group}
                  confirmed={!suggested}
                  onMouseEnter={() => onHoverSelected?.(tag.id)}
                  onMouseLeave={() => onHoverSelected?.(null)}
                  className="cursor-default transition-colors hover:bg-gray-200"
                  tooltipDelay={suggested ? 1 : undefined}
                  title={
                    suggested ? (
                      <span>
                        {pillTitles?.[tag.id] ?? 'AI-suggested tag.'} Use{' '}
                        <CheckIcon size={12} weight="bold" className="inline" /> to accept or{' '}
                        <XIcon size={12} weight="bold" className="inline" /> to decline.
                      </span>
                    ) : undefined
                  }
                  trailing={
                    suggested ? (
                      <span className="flex items-center gap-0.5">
                        {onApproveSuggestion && (
                          <Tooltip title="Confirm AI suggestion" placement="top">
                            <button
                              type="button"
                              aria-label={`Confirm ${tag.label}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                onApproveSuggestion(tag.id);
                              }}
                              className="rounded-full p-0.5 text-content-subtle transition-colors hover:bg-gray-300 hover:text-content-default"
                            >
                              <CheckIcon size={12} weight="bold" aria-hidden="true" />
                            </button>
                          </Tooltip>
                        )}
                        {onDeclineSuggestion && (
                          <Tooltip title="Decline AI suggestion" placement="top">
                            <button
                              type="button"
                              aria-label={`Decline ${tag.label}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                onDeclineSuggestion(tag.id);
                              }}
                              className="rounded-full p-0.5 text-content-subtle transition-colors hover:bg-gray-300 hover:text-content-default"
                            >
                              <XIcon size={12} weight="bold" aria-hidden="true" />
                            </button>
                          </Tooltip>
                        )}
                      </span>
                    ) : (
                      <BaseCombobox.ChipRemove
                        aria-label={`Remove ${tag.label}`}
                        className="rounded-full p-0.5 text-content-subtle transition-colors hover:bg-gray-300 hover:text-content-default"
                      >
                        <XIcon size={12} weight="bold" aria-hidden="true" />
                      </BaseCombobox.ChipRemove>
                    )
                  }
                />
              </span>
            </BaseCombobox.Chip>
          );
        })}
        {hiddenCount > 0 && (
          <span className="inline-flex shrink-0 items-center rounded-full bg-surface-subtle px-2 py-0.5 text-xs text-content-subtlest ring-1 ring-inset ring-border-subtle">
            +{hiddenCount}
          </span>
        )}
        <BaseCombobox.Input
          placeholder={selected.length === 0 ? placeholder : undefined}
          aria-label={ariaLabel ?? 'Search tags'}
          className={twMerge(
            'bg-transparent py-0.5 text-sm text-content-default outline-none placeholder:text-content-subtlest',
            singleLine && selected.length > 0 ? 'w-0 min-w-0 flex-none p-0' : 'min-w-[8ch] flex-1',
            size === 'xs' && 'text-xs'
          )}
        />
      </BaseCombobox.Chips>
      <BaseCombobox.Portal>
        <BaseCombobox.Positioner
          anchor={chipsElement}
          side="bottom"
          align="start"
          sideOffset={8}
          className="z-critical"
        >
          <BaseCombobox.Popup
            onClick={(event) => event.stopPropagation()}
            className={twMerge(
              'z-critical overflow-visible rounded-lg border-2 border-border-default bg-white shadow-lg outline-none',
              singleLine ? 'w-72' : 'w-[var(--anchor-width)]'
            )}
          >
            {singleLine && selected.length > 0 && (
              <div className="border-b border-border-subtle px-2 py-2">
                <div className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-content-subtlest">
                  Selected
                </div>
                <div className="flex max-h-40 flex-col overflow-y-auto">
                  {selected.map((tag) => {
                    const group = groups.find((item) => item.slug === tag.group);
                    if (!group) return null;
                    return (
                      <TagOptionRow
                        key={tag.id}
                        option={tag}
                        selected
                        size={size}
                        onToggle={() => toggle(group, tag.id)}
                      />
                    );
                  })}
                </div>
              </div>
            )}
            {groups.length > 1 && (
              <div className="flex items-center gap-2 border-b border-border-subtle px-2 py-2">
                <span className={twMerge('shrink-0 text-content-subtle', size === 'sm' ? 'text-sm' : 'text-xs')}>
                  Types:
                </span>
                <Dropdown
                  options={groups.map((group) => {
                    const Icon = FILTER_ICON[group.slug];
                    return {
                      value: group.slug,
                      label: group.label,
                      icon: <Icon size={size === 'sm' ? 16 : 12} weight="light" />,
                      className: 'gap-1.5 [&>label]:!mr-0'
                    };
                  })}
                  multiple
                  value={filter}
                  onChange={(next) =>
                    setFilter(Array.isArray(next) ? (next as TagPillGroup[]) : [next as TagPillGroup])
                  }
                  placeholder="All types"
                  ariaLabel="Filter tag types"
                  size={size === 'sm' ? 'md' : 'sm'}
                  width="w-full"
                  className="min-w-0 flex-1"
                />
              </div>
            )}
            <BaseCombobox.Empty
              className={twMerge(
                'empty:p-0 px-2 py-3 text-center text-content-subtlest',
                size === 'sm' ? 'text-sm' : 'text-xs'
              )}
            >
              No matching tags
            </BaseCombobox.Empty>
            <BaseCombobox.List className="custom-scrollbar-thin max-h-80 overflow-y-auto py-2 px-2 empty:p-0 ">
              {(group: (typeof selectableGroups)[number]) => (
                <BaseCombobox.Group key={group.slug} items={group.items} className="mb-2 last:mb-0">
                  <BaseCombobox.GroupLabel className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-content-subtlest">
                    {group.label}
                  </BaseCombobox.GroupLabel>
                  <BaseCombobox.Collection>
                    {(option: TagPillSelectOption & { tagGroup: TagPillSelectGroup }) => (
                      <BaseCombobox.Item
                        key={option.id}
                        value={option}
                        className="outline-none data-[highlighted]:rounded-[4px] data-[highlighted]:bg-surface-hover"
                      >
                        <TagOptionRow
                          option={option}
                          selected={false}
                          size={size}
                          onToggle={() => toggle(option.tagGroup, option.id)}
                        />
                      </BaseCombobox.Item>
                    )}
                  </BaseCombobox.Collection>
                </BaseCombobox.Group>
              )}
            </BaseCombobox.List>
          </BaseCombobox.Popup>
        </BaseCombobox.Positioner>
      </BaseCombobox.Portal>
    </BaseCombobox.Root>
  );
}
